import { z } from "zod";
import { RequestStatusSchema } from "@src/sudozi-schemas/request-status-schema";
import { BillPaymentStatusSchema } from "@src/sudozi-schemas/bill-payment-status-schema";
import { ErpVendorPaymentInformationSchema } from "@src/sudozi-schemas/erp-vendor-payment-information-schema";
import { DocumentTypeSchema } from "@src/sudozi-schemas/document-type-schema";
import { IntakeCategoryType } from "@src/sudozi-schemas/intake-submission-schemas";

export const BillSchema = z.object({
  id: z.string(),
  createdAt: z.preprocess(
    (val) => (val instanceof Date ? val.toISOString() : val),
    z.string().datetime(),
  ),
  createdBy: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  }),
  createdById: z.string(),
  erpVendorId: z.string(),
  invoiceDate: z.string(),
  invoiceNumber: z.string(),
  dueDate: z.string(),
  erpEntityId: z.string(),
  spendRequestId: z.string().nullable(),
  totalAmount: z.number(),
  accountingPeriodId: z.string().nullable(),
  description: z.string(),
  status: z.enum(["PENDING", "APPROVED", "REJECTED", "PAID"]),
  progress: z.number(),
  canEdit: z.boolean(),
  erpEntity: z.object({
    id: z.string(),
    name: z.string(),
  }),
  erpVendor: z.object({
    id: z.string(),
    name: z.string(),
    erpVendorPaymentInformation: z.array(ErpVendorPaymentInformationSchema),
  }),
  vendor: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  invoice: z.object({
    id: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    erpEntityId: z.string().nullable(),
    document: z.object({
      id: z.string(),
    }),
  }),
  accountingPeriod: z
    .object({
      id: z.string(),
      name: z.string(),
      startYearMonthDay: z.string(),
      endYearMonthDay: z.string(),
      isPosting: z.boolean(),
      isQuarter: z.boolean(),
      isYear: z.boolean(),
      isClosed: z.boolean(),
    })
    .nullable(),
  spendRequest: z
    .object({
      id: z.string(),
      readableId: z.string(),
    })
    .nullable(),
  intakeSubmission: z
    .object({
      id: z.string(),
      name: z.string(),
      submittedAt: z.string().nullish(),
      status: z.enum(["PENDING", "APPROVED", "REJECTED"]),
      number: z.number(),
      title: z.string().nullable(),
      version: z.number(),
      intakeCategory: z.object({
        id: z.string(),
        name: z.string(),
        type: IntakeCategoryType,
      }),
    })
    .nullable(),
  billPayment: z
    .object({
      id: z.string(),
      status: BillPaymentStatusSchema,
    })
    .nullable(),
  watchers: z.array(
    z.object({
      id: z.string(),
      groupId: z.string().nullable(),
      userId: z.string(),
      spendRequestId: z.string().nullable(),
      billId: z.string().nullable(),
      intakeSubmissionId: z.string().nullable(),
      user: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
    }),
  ),
  approverStatuses: z.array(
    z.object({
      id: z.string(),
      level: z.number(),
      status: RequestStatusSchema,
      comment: z.string(),
      isManagerApprover: z.boolean(),
      department: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      businessUnit: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      group: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      user: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
      delegates: z.array(
        z.object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        }),
      ),
    }),
  ),
  levelGroupStatuses: z.array(
    z.object({
      id: z.string(),
      level: z.number(),
      status: RequestStatusSchema,
      bypassedAt: z.string().nullable(),
      approvedAt: z.string().nullable(),
      approverNote: z.string(),
      approvedBy: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        })
        .nullable(),
      delegateApproverUser: z
        .object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        })
        .nullable(),
      department: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      businessUnit: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      group: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
    }),
  ),
  lines: z.array(
    z.object({
      id: z.string(),
      amount: z.number(),
      memo: z.string().nullable(),
      erpAmortizationTemplate: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      amortizationStartDate: z.string().nullable(),
      amortizationEndDate: z.string().nullable(),
      erpAccount: z.object({
        id: z.string(),
        name: z.string(),
        generalLedgerAccountNumber: z.string().nullable(),
      }),
      erpClassification: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      erpDepartmentMapping: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
    }),
  ),
  documents: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        displayName: z.string(),
        signedDownloadUrl: z.string(),
        createdAt: z.string(),
        updatedAt: z.string(),
        mimeType: z.string(),
        type: z.array(DocumentTypeSchema),
      }),
    )
    .nullable(),
  awaitingApprovalFromLevelGroupStatuses: z.array(
    z.object({
      id: z.string(),
      approvers: z.array(
        z.object({
          id: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string(),
        }),
      ),
      department: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      businessUnit: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
      group: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
    }),
  ),
});

export type Bill = z.infer<typeof BillSchema>;

export const CreateBillSchema = z.object({
  createdById: z.string(),
  newErpVendorName: z.string().optional(),
  newErpVendorEmail: z.string().optional(),
  shouldCreateSudoziVendor: z.boolean().optional(),
  ownerId: z.string().optional(),
  intakeSubmissionId: z.string().optional(),
  invoiceId: z.string().optional(),
  erpVendorId: z.string().nullable(),
  erpEntityId: z.string(),
  invoiceNumber: z.string(),
  spendRequestId: z.string().nullable(),
  invoiceDate: z.string(),
  totalAmount: z.number(),
  accountingPeriodId: z.string().nullable(),
  dueDate: z.string(),
  description: z.string(),
  erpAmortizationTemplateId: z.string().nullish(),
  amortizationStartDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish(),
  amortizationEndDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish(),
  lines: z.array(
    z.object({
      amount: z.number(),
      memo: z.string().nullable(),
      erpAccountId: z.string(),
      erpBusinessUnitId: z.string().nullable(),
      erpDepartmentMappingId: z.string().nullable(),
      erpAmortizationTemplateId: z.string().nullish(),
      amortizationStartDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullish(),
      amortizationEndDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullish(),
    }),
  ),
});
